import { Component, Input, Output, EventEmitter, inject } from '@angular/core';
import { Dialog } from 'primeng/dialog';
import { Button } from 'primeng/button';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InputText } from 'primeng/inputtext';
import { Blueprint } from '../../../models/blueprint.model';
import { BlueprintItemDataEditorService } from '../blueprint-item-data-editor/blueprint-item-data-editor.service';
import { DragDropModule } from 'primeng/dragdrop';
import { BlueprintItemDataEditorComponent } from '../blueprint-item-data-editor/blueprint-item-data-editor.component';
import { BlueprintItemWrite } from '../../../models/blueprint-item.model';
import { SettingsService } from '../../settings-page/settings-service/settings.service';
import { map, take } from 'rxjs';
import { MaterialSelectComponent } from '../../shared/material-select/material-select.component';
import { MessageModule } from 'primeng/message';
import { EquipmentService } from '../../equipment.service';
import { ConsumableService } from '../../consumable.service';
import {
  CIRCULAR_BLUEPRINT_REFERENCE_MESSAGE,
  linkedBlueprintCreatesCycle,
} from '../blueprint-item-data-editor/blueprint-allowed-to-add.util';

@Component({
  selector: 'app-blueprint-editor',
  imports: [
    Button,
    Dialog,
    CommonModule,
    FormsModule,
    InputText,
    DragDropModule,
    BlueprintItemDataEditorComponent,
    MaterialSelectComponent,
    MessageModule,
  ],
  providers: [BlueprintItemDataEditorService],
  templateUrl: './blueprint-editor.component.html',
  styleUrl: './blueprint-editor.component.scss',
})
export class BlueprintEditorComponent {
  quoteItemDataEditorService: BlueprintItemDataEditorService = inject(
    BlueprintItemDataEditorService,
  );

  settingsService = inject(SettingsService);
  equipmentService = inject(EquipmentService);
  consumableService = inject(ConsumableService);

  currencyCode$ = this.settingsService.settings$.pipe(
    map((settings) => settings?.organisation_base_currency_code || 'GBP'),
  );
  tradeStudyToolFeaturesEnabled$ =
    this.settingsService.isTradeStudyToolFeaturesEnabled$();
  consumablesEnabled$ = this.settingsService.isConsumablesEnabled$();
  activeEquipment$ = this.equipmentService.activeEquipment$;
  activeConsumables$ = this.consumableService.activeConsumables$;

  @Input()
  visible: boolean = false;

  @Output()
  visibleChange = new EventEmitter<boolean>();

  @Input()
  blueprint: Blueprint | null = null; // null = create new, otherwise edit existing

  @Input()
  allBlueprints: Blueprint[] = []; // All blueprints passed from parent to avoid multiple API calls

  blueprintName: string = '';
  defaultMaterialId: number | null = null;
  submitError: string | null = null;

  get isEditMode(): boolean {
    return this.blueprint !== null;
  }

  showDialog(): void {
    this.submitError = null;
    void this.equipmentService.loadEquipment();
    this.settingsService
      .isConsumablesEnabled$()
      .pipe(take(1))
      .subscribe((enabled) => {
        if (enabled) {
          void this.consumableService.loadConsumables();
        }
      });
    if (this.blueprint) {
      // Edit mode: load existing blueprint
      this.blueprintName = this.blueprint.name;
      this.defaultMaterialId = this.blueprint.default_material_id ?? null;
      if (this.blueprint.items && this.blueprint.items.length > 0) {
        this.quoteItemDataEditorService.loadBlueprintItems(
          this.blueprint,
          this.allBlueprints,
        );
      } else {
        this.quoteItemDataEditorService.clearItems();
      }
    } else {
      // Create mode: start fresh
      this.blueprintName = '';
      this.defaultMaterialId = null;
      this.quoteItemDataEditorService.clearItems();
    }
  }

  onVisibleChange(visible: boolean): void {
    this.visible = visible;
    this.visibleChange.emit(visible);
    if (!visible) {
      this.quoteItemDataEditorService.clearItems();
      this.dialogClosed.emit();
    }
  }

  closeDialog(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.quoteItemDataEditorService.clearItems();
    this.dialogClosed.emit();
  }

  async onSubmit(): Promise<void> {
    if (!this.blueprintName.trim()) {
      this.submitError = 'Please enter a blueprint name.';
      return;
    }

    const items = this.quoteItemDataEditorService.toBlueprintItems();
    if (
      items.some((item) =>
        linkedBlueprintCreatesCycle(
          this.blueprint,
          this.allBlueprints,
          item.linked_to_blueprint_id,
        ),
      )
    ) {
      this.submitError = CIRCULAR_BLUEPRINT_REFERENCE_MESSAGE;
      return;
    }

    this.submitError = null;

    this.submitBlueprint.emit({
      id: this.blueprint?.id || null,
      name: this.blueprintName,
      items: items,
      default_material_id: this.defaultMaterialId,
    });
  }

  @Output()
  submitBlueprint = new EventEmitter<{
    id: string | null;
    name: string;
    items: BlueprintItemWrite[];
    default_material_id: number | null;
  }>();

  @Output()
  dialogClosed = new EventEmitter<void>();
}
